import { SelectProductsV2 } from "@components/auto-completes/products-v2";
import { SHBooleanField, SHStack } from "@components/design-systems";
import { LocalStorage } from "@constants";
import { useIsNew } from "@hooks/useIsNew";
import { useLocalStorage } from "@hooks/useLocalStorage";
import { usePlatformAnalysis } from "@hooks/usePlatformAnalysis";
import {
  AnalysisSetupDTO,
  AnalysisSetupStepRenderedFieldProps,
} from "@models/platform-analysis/entities/steps/setup";
import { ProductLOV } from "@models/product/entities/product";
import { isNil } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useParams } from "react-router";
import { useEffectOnce, useToggle } from "react-use";
import { ConfirmRefreshAnalysisDataDialog } from "../../../../../components/dialogs/confirm-refresh-platforms";

export const AnalysisSetupAdviserMainPlatformsSelection = ({
  disabled,
}: AnalysisSetupStepRenderedFieldProps) => {
  const isNew = useIsNew();
  const { analysisId } = useParams<{ analysisId: string }>();
  const {
    control,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<AnalysisSetupDTO>();
  const { getLocalStorageItem, setLocalStorageItem } = useLocalStorage();
  const reachAnalysisSetupPageLocalStorage = getLocalStorageItem(
    LocalStorage.PlatformAnalysisReachAnalysisSetupPage,
  );
  const {
    reachAnalysisSetupPage,
    setReachAnalysisSetupPage,
    loadPlatformAnalysisProducts,
  } = usePlatformAnalysis();

  const watchedHasMainProduct = !!watch("hasMainProduct");
  const watchedMainProductId = watch("mainProductId");

  //State
  const dialogResultResolverRef = useRef<((result: boolean) => void) | null>(
    null,
  );
  const [refreshPlatformDialogOpen, toggleRefreshPlatformDialogOpen] =
    useToggle(false);
  const [platformSelections, setPlatformSelections] = useState<ProductLOV[]>(
    [],
  );

  const shouldShowRefreshDialog = useMemo(
    () =>
      reachAnalysisSetupPageLocalStorage === "FIRST_TIME" ||
      reachAnalysisSetupPage === "FIRST_TIME",
    [reachAnalysisSetupPage, reachAnalysisSetupPageLocalStorage],
  );

  // Functions
  const getRefreshPlatformsDialogResult = (): Promise<boolean> => {
    toggleRefreshPlatformDialogOpen(true);
    return new Promise((resolve) => {
      dialogResultResolverRef.current = (res: boolean) => {
        resolve(res);
        dialogResultResolverRef.current = null;
      };
    });
  };

  const onRefreshDialogClose = () => {
    toggleRefreshPlatformDialogOpen(false);
    dialogResultResolverRef?.current?.(false);
  };

  const onRefreshDialogSubmit = () => {
    toggleRefreshPlatformDialogOpen(false);
    dialogResultResolverRef.current?.(true);
  };

  const handleOnRefreshPlatforms = () => {
    setValue("hasMainProduct", false);
    setValue("mainProductId", null);
    setValue("hasRateCard", false);
    setValue("selectRateCards", []);
    toggleRefreshPlatformDialogOpen(false);
  };

  const setReachSecondTime = () => {
    setReachAnalysisSetupPage("SECOND_TIME");
    setLocalStorageItem(
      LocalStorage.PlatformAnalysisReachAnalysisSetupPage,
      "SECOND_TIME",
    );
  };

  const handleOnHasMainPlatformChange = async () => {
    if (!shouldShowRefreshDialog) {
      setValue("mainProductId", null);
      return false;
    }

    setReachSecondTime();
    const isRefresh = await getRefreshPlatformsDialogResult();
    if (!isRefresh) return true;
    handleOnRefreshPlatforms();
    return false;
  };

  const handleOnMainPlatformChange = async (platformId: string | null) => {
    if (!shouldShowRefreshDialog) return platformId;
    setReachSecondTime();
    const isRefresh = await getRefreshPlatformsDialogResult();
    if (!isRefresh) return watchedMainProductId;
    return platformId;
  };

  // Effects
  useEffectOnce(() => {
    loadPlatformAnalysisProducts({
      platformAnalysisId: analysisId || "new",
    }).then((data) => {
      setPlatformSelections(data ?? []);
    });
  });

  useEffect(() => {
    const currentOtherPlatforms = getValues("otherProductIds") || [];
    const newValue = currentOtherPlatforms.filter(
      (item) => item !== watchedMainProductId,
    );
    setValue("otherProductIds", newValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedMainProductId]);

  useEffect(() => {
    if (watchedHasMainProduct) return;
    setValue("mainProductId", null, {
      shouldDirty: true,
      shouldValidate: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedHasMainProduct]);

  return (
    <SHStack spacing={3}>
      <Controller
        name={"hasMainProduct"}
        control={control}
        render={({ field: { onChange, ...other } }) => (
          <SHBooleanField
            {...other}
            isSwitch
            isShowClearButton={false}
            value={other.value}
            disabled={disabled}
            onChange={async (_, value?: boolean | null) => {
              if (!!value || !getValues("mainProductId") || isNew) {
                setValue("mainProductId", null);
                onChange(value);
                return;
              }

              const result = await handleOnHasMainPlatformChange();
              onChange(result);
            }}
            label={"Would you like to select a current platform?"}
          />
        )}
      />

      {watchedHasMainProduct && (
        <Controller
          control={control}
          name={"mainProductId"}
          render={({ field: { onChange, ...others } }) => {
            return (
              <SelectProductsV2
                {...others}
                onChange={async (platformId) => {
                  if (typeof platformId !== "string" && !isNil(platformId))
                    return;

                  const changeResult = await handleOnMainPlatformChange(
                    platformId,
                  );
                  onChange(changeResult);
                }}
                selectedIds={watchedMainProductId}
                disabled={disabled}
                options={platformSelections}
                textFieldProps={{
                  label: "Select one from below",
                  placeholder: "Search for platform",
                  required: true,
                  error: !!errors?.mainProductId,
                  helperText: errors?.mainProductId?.message,
                }}
              />
            );
          }}
        />
      )}

      <ConfirmRefreshAnalysisDataDialog
        open={refreshPlatformDialogOpen}
        onClose={onRefreshDialogClose}
        onSubmit={onRefreshDialogSubmit}
      />
    </SHStack>
  );
};
